import type { Descendant } from '@udecode/plate';
import { usePluginOption } from '@udecode/plate/react';
import { useEffect, useRef } from 'react';

import type { AIChatPluginConfig } from '../AIChatPlugin';
import { useLastAssistantMessage } from '../utils/getLastAssistantMessage';

export const useChatChunk = ({
    onChunk,
    onFinish,
}: {
    onChunk: (chunk: { isFirst: boolean; nodes: Descendant[]; text: string }) => void;
    onFinish?: ({ content }: { content: string }) => void;
}) => {
    const { status } = usePluginOption(
      { key: 'aiChat' } as AIChatPluginConfig,
      'chat'
    );
    const isLoading = status === 'streaming' || status === 'submitted';

    const lastMessage = useLastAssistantMessage();

    const content = lastMessage ? lastMessage.parts
        .filter(part => part.type === 'text')
        .map(part => part.type === 'text' ? part.text : '')
        .join('') : '';

    const insertedTextRef = useRef<string>('');
    const prevIsLoadingRef = useRef(isLoading);

    useEffect(() => {
        if (!isLoading) {
            insertedTextRef.current = '';
        }
        if (prevIsLoadingRef.current && !isLoading) {
            onFinish?.({ content: content ?? '' });
        }

        prevIsLoadingRef.current = isLoading;
    }, [isLoading]);

    useEffect(() => {
        if (!content) {
            return;
        }

        const chunk = content.slice(insertedTextRef.current.length);

        if (chunk) {
            const isFirst = insertedTextRef.current === '';

            onChunk({
                isFirst,
                nodes: [{ text: chunk }],
                text: content,
            });
        }

        insertedTextRef.current = content;
    }, [content]);
};
